/**
 * Settings > Installed Apps: the tenant's apps from GET /apps/manifests, one card per app key, with its versions,
 * what each version changed, links to its menu entries, and (for admins) switching versions on and off through the
 * admin manifest routes. Everyone else sees the same list read-only.
 */
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { ApiError } from '@diggens/api-client';
import { formatDateTime } from '@diggens/shared-utils';
import { useCurrentUser } from '@diggens/user-management';
import { ICON_SIZES, ICON_STROKE } from '../constants/icons';
import { useToast } from '../contexts/ToastContext';
import { describeActivation, groupInstalledApps, STATUS_LABELS, useActivateAppVersion, useDisableAppVersion, useInstalledApps, type AppVersionView, type InstalledAppView, type TenantAppStatus } from '../features/apps';

/** The roles the API's ADMIN_ROLES accept on the manifest writes. */
const APP_ADMIN_ROLES = ['admin', 'super_admin'];

const STATUS_COLORS: Record<TenantAppStatus, 'success' | 'default' | 'warning'> = {
  active: 'success',
  disabled: 'default',
  draft: 'warning',
};

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.detail ? `${error.message}: ${error.detail}` : error.message;
  }
  return error instanceof Error ? error.message : 'Something went wrong';
}

interface PendingAction {
  kind: 'activate' | 'disable';
  app: InstalledAppView;
  version: AppVersionView;
}

function describePending(action: PendingAction): { title: string; body: string; confirm: string } {
  const { app, version } = action;
  if (action.kind === 'disable') {
    return {
      title: `Disable ${app.name} v${version.version}?`,
      body: `Its menu entries, lists and forms disappear for everyone in the workspace. Records stay stored and come back when a version is active again.`,
      confirm: 'Disable',
    };
  }
  const replacing = app.activeVersion && app.activeVersion.id !== version.id ? ` Version ${app.activeVersion.version}, active now, is disabled in the same step.` : '';
  return {
    title: `Activate ${app.name} v${version.version}?`,
    body: `Its types, views and menu entries go live for everyone in the workspace.${replacing} Picklists it declares are created where the workspace lacks them, and existing ones gain the items they lack.`,
    confirm: 'Activate',
  };
}

export function InstalledApps() {
  const { data: user } = useCurrentUser();
  const isAdmin = !!user?.role && APP_ADMIN_ROLES.includes(user.role);
  const { data: records, isLoading, isError, error } = useInstalledApps({ enabled: !!user });
  const apps = records ? groupInstalledApps(records) : [];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (isError) {
    return <Alert severity="error">Could not load the installed apps: {errorMessage(error)}</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Apps the Developer built for this workspace. Each one is a versioned manifest of types, views and menu entries; one version of an app is live at a time.
        {isAdmin ? ' As an admin you can switch a version off, bring a disabled one back, or activate a draft.' : ' Only admins can change which version is live.'}
      </Typography>
      {apps.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="subtitle1" gutterBottom>
            No apps installed
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ask Chief of Staff for an app in Diggens; the Developer proposes a manifest and an admin approves the install.
          </Typography>
        </Paper>
      ) : (
        apps.map((app) => <InstalledAppCard key={app.key} app={app} isAdmin={isAdmin} />)
      )}
    </Stack>
  );
}

function SummaryLine({ label, items }: { label: string; items: readonly string[] }) {
  return (
    <Typography variant="body2" sx={{ display: 'flex', gap: 1 }}>
      <Box component="span" sx={{ color: 'text.secondary', minWidth: 110 }}>
        {label}
      </Box>
      <Box component="span">{items.length > 0 ? items.join(', ') : 'none'}</Box>
    </Typography>
  );
}

function InstalledAppCard({ app, isAdmin }: { app: InstalledAppView; isAdmin: boolean }) {
  const [showVersions, setShowVersions] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const { showToast } = useToast();
  const activate = useActivateAppVersion();
  const disable = useDisableAppVersion();
  const busy = activate.isPending || disable.isPending;

  const confirmPending = async () => {
    if (!pending) return;
    const action = pending;
    setPending(null);
    try {
      if (action.kind === 'activate') {
        const result = await activate.mutateAsync(action.version.id);
        showToast(describeActivation(result), 'success');
      } else {
        await disable.mutateAsync(action.version.id);
        showToast(`${action.app.name} v${action.version.version} is disabled`, 'success');
      }
    } catch (err) {
      showToast(errorMessage(err), 'error');
    }
  };

  const summary = app.summary;
  const dialog = pending ? describePending(pending) : null;

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }} data-testid={`installed-app-${app.key}`}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6">{app.name}</Typography>
            <Chip size="small" label={STATUS_LABELS[app.status]} color={STATUS_COLORS[app.status]} />
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {app.key}
            </Typography>
          </Box>
          {app.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {app.description}
            </Typography>
          )}
        </Box>
        {isAdmin && app.activeVersion && (
          <Button variant="outlined" color="inherit" size="small" disabled={busy} onClick={() => setPending({ kind: 'disable', app, version: app.activeVersion! })}>
            Disable v{app.activeVersion.version}
          </Button>
        )}
      </Box>

      <Stack spacing={0.5} sx={{ mt: 2 }}>
        <SummaryLine label="Active version" items={app.activeVersion ? [`v${app.activeVersion.version}`] : app.status === 'draft' ? ['none (draft only)'] : ['none (disabled)']} />
        {app.activeVersion && <SummaryLine label="Installed" items={[`${app.installedAt ? formatDateTime(app.installedAt) : 'unknown time'} by ${app.installedBy ?? 'Unknown'}`]} />}
        {summary && (
          <>
            <SummaryLine label="Types" items={summary.types.map((type) => (summary.overrides.some((key) => type === key) ? `${type} (redefines built-in)` : type))} />
            <SummaryLine label="Views" items={summary.views} />
            <SummaryLine label="Menu" items={summary.nav} />
          </>
        )}
        {!summary && (
          <Typography variant="body2" color="warning.main">
            The stored manifest of this app no longer parses; its versions are listed below but cannot be described.
          </Typography>
        )}
      </Stack>

      {app.navLinks.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
            Open in the CRM
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
            {app.navLinks.map((link) => (
              <Chip
                key={link.path}
                component={RouterLink}
                to={link.path}
                clickable
                size="small"
                variant="outlined"
                icon={<ExternalLink size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />}
                label={`${link.label} (${link.path})`}
              />
            ))}
          </Box>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      <Button size="small" color="inherit" onClick={() => setShowVersions((open) => !open)} startIcon={showVersions ? <ChevronDown size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} /> : <ChevronRight size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />}>
        {app.versions.length} {app.versions.length === 1 ? 'version' : 'versions'}
      </Button>
      <Collapse in={showVersions} timeout="auto" unmountOnExit>
        <List dense disablePadding sx={{ mt: 1 }}>
          {app.versions.map((version) => (
            <VersionRow key={version.id} app={app} version={version} isAdmin={isAdmin} busy={busy} onAction={setPending} />
          ))}
        </List>
      </Collapse>

      <Dialog open={!!pending} onClose={() => setPending(null)}>
        {dialog && (
          <>
            <DialogTitle>{dialog.title}</DialogTitle>
            <DialogContent>
              <DialogContentText>{dialog.body}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPending(null)}>Cancel</Button>
              <Button variant="contained" color={pending?.kind === 'disable' ? 'warning' : 'primary'} onClick={confirmPending}>
                {dialog.confirm}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Paper>
  );
}

function VersionRow({ app, version, isAdmin, busy, onAction }: { app: InstalledAppView; version: AppVersionView; isAdmin: boolean; busy: boolean; onAction: (action: PendingAction) => void }) {
  const canActivate = isAdmin && version.manifest && version.status !== 'active';
  const canDisable = isAdmin && version.status === 'active';
  const activateLabel = version.status === 'disabled' ? 'Re-activate' : 'Activate';
  return (
    <ListItem
      disableGutters
      alignItems="flex-start"
      sx={{ py: 1, borderTop: 1, borderColor: 'divider' }}
      secondaryAction={
        isAdmin ? (
          <Stack direction="row" spacing={1}>
            {canDisable && (
              <Button size="small" color="inherit" variant="outlined" disabled={busy} onClick={() => onAction({ kind: 'disable', app, version })}>
                Disable
              </Button>
            )}
            {canActivate && (
              <Tooltip title={version.status === 'disabled' ? 'Make this version live again in place of the active one' : 'Make this draft the live version'}>
                <span>
                  <Button size="small" variant="contained" disabled={busy} onClick={() => onAction({ kind: 'activate', app, version })}>
                    {activateLabel}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Stack>
        ) : undefined
      }
    >
      <ListItemText
        sx={{ pr: isAdmin ? 24 : 0 }}
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2">v{version.version}</Typography>
            <Chip size="small" label={STATUS_LABELS[version.status]} color={STATUS_COLORS[version.status]} />
            <Typography variant="caption" color="text.secondary">
              stored {formatDateTime(version.createdAt)} by {version.createdBy}
            </Typography>
          </Box>
        }
        secondary={
          <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
            {!version.manifest ? (
              <Typography variant="body2" color="warning.main" component="span">
                Stored manifest no longer parses.
              </Typography>
            ) : version.isFirst ? (
              <Typography variant="body2" color="text.secondary" component="span">
                First version.
              </Typography>
            ) : version.changes.length === 0 ? (
              <Typography variant="body2" color="text.secondary" component="span">
                No changes by key against v{version.version - 1}.
              </Typography>
            ) : (
              <Box component="span" sx={{ display: 'block' }}>
                <Typography variant="body2" color="text.secondary" component="span">
                  Against the previous version:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {version.changes.map((line) => (
                    <Typography key={line} component="li" variant="body2">
                      {line}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        }
        slotProps={{ secondary: { component: 'div' } }}
      />
    </ListItem>
  );
}
